/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type { FeatureAppDefinition, FeatureServices } from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import I18nContextComponent from '@oneaudi/i18n-context';
import { TrackingContext } from './context';
import FeatureApp from './FeatureApp';
import type { PartnerId } from './types/partner-id';
import { ContentContextProvider } from './context/ContentContext';
import { getDesignLibrary } from '../alpha-helper';
import type {
  LocaleService,
  GfaLocaleService,
  TrackingService,
  AsyncSsrManager,
  AudiFootnoteRefernceServiceScopeManagerInterface,
  AudiMarketContextService,
  I18NService,
  SerializedStateManager,
  LayerManager,
  ContentService,
} from './feature-service-types';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 'dbad:audi-i18n-service': I18NService;
  readonly 'gfa:locale-service': GfaLocaleService;
  readonly 'locale-service': LocaleService;
  readonly 'audi-tracking-service'?: TrackingService;
  readonly 'layer-manager': LayerManager | undefined;
  readonly 's2:async-ssr-manager': AsyncSsrManager | undefined;
  readonly 'audi-market-context-service': AudiMarketContextService | undefined;
  readonly 's2:serialized-state-manager': SerializedStateManager;
  readonly 's2:logger': Logger;
  readonly 'audi-content-service': ContentService;
}

// Type to work with the microkernel that maybe is available globaly
declare global {
  interface Window {
    microkernel: {
      stateRegistry: {
        subscribeToStore: (name: string, callback: (state: PartnerId) => void) => void;
        unsubscribeFromStore: (name: string, callback: (state: PartnerId) => void) => void;
      };
    };
  }
}

export type AudiFooterConfig = {
  api?: string;
  enablePartnerIdReplacement?: boolean;
  hideSocialMedia?: boolean;
};

type FeatureHubAppDefinitionType = FeatureAppDefinition<
  ReactFeatureApp,
  FeatureServiceDependencies,
  AudiFooterConfig
>;

const featureAppDefinition: FeatureHubAppDefinitionType = {
  dependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'layer-manager': '^2.5.0',
      'locale-service': '^1.0.0',
      'audi-footnote-reference-service': '3.0.0',
    },
    externals: {
      react: '^17.0.2 || ^18.2.0',
      'styled-components': '^5.3.11',
      '@feature-hub/react': '^3',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      'audi-market-context-service': '^2.0.0',
    },
  },
  create: ({ baseUrl, featureServices, featureAppId }) => {
    const logger = featureServices['s2:logger'];
    logger?.info('Feature App created.', featureServices);

    if (!baseUrl) {
      throw new Error(`The feature app's baseUrl is undefined.`);
    }

    const { countryCode } = featureServices['gfa:locale-service'];
    const i18nData: I18NService = featureServices['dbad:audi-i18n-service'];

    const referenceServiceScopeManager = featureServices[
      'audi-footnote-reference-service'
    ] as AudiFootnoteRefernceServiceScopeManagerInterface;
    const layerManager = featureServices['layer-manager'];
    const localeService = featureServices['locale-service'];
    const contentService = featureServices['audi-content-service'];
    const trackingService = featureServices['audi-tracking-service'];
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    const referenceServiceManager = referenceServiceScopeManager
      ? referenceServiceScopeManager.getDefaultScopeRefService()
      : undefined;

    return {
      render: (): React.ReactNode => (
        <ThemeProvider theme={{ name: 'dark' }}>
          <ContentContextProvider contentService={contentService}>
            <I18nContextComponent
              /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
              // @ts-ignore
              featureServices={featureServices}
              i18nData={i18nData}
              scopes={['nemo.common', 'fa.audi-basket']}
            >
              {/* @ts-expect-error optional dependency */}
              <TrackingContext.Provider value={trackingService}>
                <FeatureApp
                  localeService={localeService}
                  countryCode={countryCode}
                  featureAppId={featureAppId}
                  referenceServiceManager={referenceServiceManager}
                  layerManager={layerManager}
                  logger={logger}
                />
              </TrackingContext.Provider>
            </I18nContextComponent>
          </ContentContextProvider>
        </ThemeProvider>
      ),
    };
  },
};
export default featureAppDefinition;
